import { Plus } from "lucide-react";
import * as React from "react";

import { useAnalytics } from "@/providers/analytics";
import { Tag, TagDraftInput, TagInput } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { type EventName, type EventProps } from "@/shared/services/analytics";

import { type AutocompleteType } from "../TagAndMetadataAutocomplete";
import { useElementsForAutocompleteType } from "../TagAndMetadataAutocomplete/hook";

import styles from "./TagsEditor.module.css";

/** A typed tag as it is saved: trimmed, with spaces as dashes. */
function normalizeTag(text: string): string {
  return text.trim().replace(/\s+/g, "-");
}

/**
 * The events an editor fires as a tag is added or removed, with their shared
 * properties: `AddSessionTagClicked`/`RemoveSessionTagClicked` and
 * `{ sessionId }`, say.
 */
export interface TagsEditorAnalytics<Name extends EventName = EventName> {
  add: Name;
  remove: Name;
  props: EventProps<Name>;
}

export interface TagsEditorProps<Name extends EventName = EventName> {
  /** The record's tags. Shown sorted A–Z. */
  tags: readonly string[];
  /** Where suggestions come from: the org's tags for this kind of record. */
  autocompleteType: AutocompleteType;
  /**
   * Adds one tag. In `live` mode a returned promise is the save: the new tag
   * shows dimmed until it settles, and is taken back if it rejects.
   */
  onAdd: (tag: string) => Promise<unknown> | void;
  /** Removes one tag, as `onAdd` adds one. */
  onRemove: (tag: string) => Promise<unknown> | void;
  /** Shows the tags with no way to change them; "None" when there are none. */
  readonly?: boolean;
  /**
   * `live` saves each change as it is made (Details tabs); `controlled` hands
   * each to the caller's form state, saved with the form.
   * @default "live"
   */
  mode?: "live" | "controlled";
  /**
   * `inline`: the tags with a dashed "+ Tag" pill that opens a small input in
   * place, no box around them (Details tabs). `field`: a bordered box the
   * size of a form's text fields, with the tags and the input inside (create
   * forms).
   * @default "inline"
   */
  variant?: "inline" | "field";
  /** Fired by the editor for each tag added or removed. */
  analytics?: TagsEditorAnalytics<Name>;
  /**
   * Identifies the record. A new one starts the editor afresh, closing the
   * input and clearing an error.
   */
  recordKey?: string;
  /** Names the tags and their input for assistive tech. @default "Tags" */
  "aria-label"?: string;
  /** Keeps the tags from changing, as while the form is saving. */
  disabled?: boolean;
}

/**
 * Adds and removes a record's tags, the same way wherever tags are edited:
 * the tags A–Z as pills, each with a × on hover, then "+ Tag". Only how a
 * change is saved differs between records, through `onAdd` and `onRemove`.
 * A change that fails to save is taken back and said so under the tags,
 * with Retry.
 */
export function TagsEditor<Name extends EventName>(
  props: TagsEditorProps<Name>,
) {
  return <Editor key={props.recordKey} {...props} />;
}

type Change = { kind: "add" | "remove"; tag: string };

function Editor<Name extends EventName>({
  tags,
  autocompleteType,
  onAdd,
  onRemove,
  readonly = false,
  mode = "live",
  variant = "inline",
  analytics,
  "aria-label": ariaLabel = "Tags",
  disabled = false,
}: TagsEditorProps<Name>) {
  const { trackEvent } = useAnalytics();
  // Changes being saved, and saved ones the record doesn't show yet.
  const [pending, setPending] = React.useState<Change[]>([]);
  const [saved, setSaved] = React.useState<Change[]>([]);
  const [failure, setFailure] = React.useState<{
    change: Change;
    message: React.ReactNode;
  } | null>(null);
  const [adding, setAdding] = React.useState(false);

  const tagsKey = tags.join("\n");
  React.useEffect(() => setSaved([]), [tagsKey]);

  const shown = React.useMemo(() => {
    const set = new Set(tags);
    for (const change of [...saved, ...pending]) {
      if (change.kind === "add") {
        set.add(change.tag);
      } else {
        set.delete(change.tag);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tags, saved, pending]);
  const busyTags = new Set(
    pending.filter((change) => change.kind === "add").map((c) => c.tag),
  );
  const saving = pending.length > 0;

  const { elements: suggestions } = useElementsForAutocompleteType(
    autocompleteType,
    [...shown],
  );

  const apply = async (change: Change) => {
    if (analytics) {
      // `props` belongs to both events, which TypeScript can't check through
      // trackEvent's per-event rest parameter while `Name` is generic.
      const track = trackEvent as (name: EventName, props: unknown) => void;
      track(
        change.kind === "add" ? analytics.add : analytics.remove,
        analytics.props,
      );
    }
    const result =
      change.kind === "add" ? onAdd(change.tag) : onRemove(change.tag);
    if (mode === "controlled" || !(result instanceof Promise)) {
      return;
    }
    setFailure(null);
    setPending((current) => [...current, change]);
    try {
      await result;
      setSaved((current) => [...current, change]);
    } catch (caught) {
      const verb = change.kind === "add" ? "add" : "remove";
      setFailure({
        change,
        message: userFacingErrorMessage(
          caught,
          `Couldn't ${verb} “${change.tag}”.`,
        ),
      });
    } finally {
      setPending((current) => current.filter((c) => c !== change));
    }
  };

  const add = (typed: string) => {
    const tag = normalizeTag(typed);
    if (tag && !shown.includes(tag)) {
      void apply({ kind: "add", tag });
    }
  };
  const remove = (tag: string) => void apply({ kind: "remove", tag });

  const pills = shown.map((tag) => (
    <Tag
      key={tag}
      label={tag}
      busy={busyTags.has(tag)}
      onDelete={readonly || disabled ? undefined : () => remove(tag)}
      deleteOnHover={variant === "inline"}
    />
  ));

  const error = failure && (
    <p className={styles.error} role="alert">
      {failure.message}{" "}
      <button
        type="button"
        className={styles.retry}
        onClick={() => void apply(failure.change)}
      >
        Retry
      </button>
    </p>
  );

  if (readonly) {
    return shown.length === 0 ? (
      <span className={styles.none}>None</span>
    ) : (
      <div className={styles.row} role="group" aria-label={ariaLabel}>
        {pills}
      </div>
    );
  }

  if (variant === "field") {
    // TagInput reports the whole list; one tag differs from `shown` each time.
    const onChange = (next: string[]) => {
      next.filter((tag) => !shown.includes(tag)).forEach(add);
      shown.filter((tag) => !next.includes(tag)).forEach(remove);
    };
    return (
      <div className={styles.editor}>
        <TagInput
          tags={[...shown]}
          onChange={onChange}
          busyTags={[...busyTags]}
          suggestions={suggestions}
          aria-label={ariaLabel}
          disabled={disabled}
        />
        {error}
      </div>
    );
  }

  return (
    <div className={styles.editor}>
      <div className={styles.row} role="group" aria-label={ariaLabel}>
        {pills}
        {adding ? (
          <TagDraftInput
            tags={shown}
            suggestions={suggestions}
            onCommit={add}
            onEscape={() => setAdding(false)}
            onLeave={() => setAdding(false)}
            aria-label={`Add a tag to ${ariaLabel.toLowerCase()}`}
            autoFocus
            className={styles.inlineInput}
            rootClassName={styles.draftRoot}
          />
        ) : (
          <button
            type="button"
            // With no tags yet, a quiet "+ Add tag" link, as in the header.
            className={shown.length === 0 ? styles.addLink : styles.addPill}
            onClick={() => setAdding(true)}
            disabled={disabled || saving}
          >
            <Plus aria-hidden className={styles.plusIcon} />
            {shown.length === 0 ? "Add tag" : "Tag"}
          </button>
        )}
      </div>
      {error}
    </div>
  );
}
